import type { ReactNode } from "react";

/**
 * The world the egg sits in, drawn entirely in CSS.
 *
 * No images and no SVG anywhere in this skin — the mountain is a clip-path
 * triangle, the snowcap a jagged polygon, the speckles and the cloud sea
 * radial gradients. That is the design's own constraint and it is worth
 * keeping: nothing here needs a network request, and every shape stays
 * scalable and themeable from the token block.
 *
 * The landing and building screens show the same peak at different sizes, so
 * the pieces take their dimensions as props rather than being duplicated at two
 * scales. Everything is `pointer-events-none` and sits behind the content: this
 * is scenery, and none of it is ever the thing to click.
 */

/**
 * The frame every hatch screen sits in: sky behind, cloud sea in front.
 *
 * Extracted mainly so `hatch-skin` cannot be forgotten. That class is what the
 * reduced-motion rule in index.css hangs off — the rule is written as a sweep
 * over the skin's root precisely because the animations are arbitrary values
 * with no class names to enumerate, so a screen that omitted the root would
 * silently ignore the setting rather than fail visibly.
 */
export function HatchScreen({
  fill = false,
  children,
}: {
  /**
   * Exactly viewport height, with the panes inside scrolling rather than the
   * page. The building screen needs it and the other two must not have it.
   *
   * That is not styling. With a minimum height instead of a fixed one, a pane
   * containing a growing build log grows the document rather than scrolling
   * inside itself, and a long build becomes an unbounded page where the whole
   * history is on screen at once and "now" is impossible to find — the same
   * reasoning, and the same bug, as the classic build view's frame.
   *
   * The landing and done screens are the other case: their content is a fixed
   * amount that can exceed a short window, and clamping them to the viewport
   * would clip a prompt gallery rather than let it scroll.
   */
  fill?: boolean;
  children: ReactNode;
}) {
  return (
    // `overflow-x-clip` is load-bearing on narrow viewports. Several pieces of
    // this design deliberately reach outside their box — the cloud sea starts at
    // `-left-[30px]`, and the done screen's shell fragments start 70px either
    // side of the headline and then fly a further 100px outward. On a 390px
    // phone that made the document 535px wide: the page scrolled sideways and
    // every centred thing on it sat off-centre. Clipping the axis costs the
    // design nothing, because nothing in it is meant to be reached by scrolling
    // right. `clip` rather than `hidden` so this never becomes a scroll
    // container, which would break the `fixed` scenery's anchoring.
    <div
      className={`hatch-skin relative isolate flex flex-col overflow-x-clip font-nunito text-bark ${
        fill ? "h-dvh overflow-y-hidden" : "min-h-dvh"
      }`}
    >
      <Sky />
      {children}
      <CloudSeaFront />
    </div>
  );
}

/**
 * The header both post-landing screens share: glyph, logo, prompt, and one
 * changing slot on the right.
 *
 * Lives here rather than beside the build view because the done screen needs it
 * too, and importing it from there made a cycle — BuildView pulls in DoneView,
 * DoneView pulled Header back out of BuildView. That only worked because
 * `Header` is a hoisted function declaration; turning it into a `const` arrow
 * would have made the done screen a temporal-dead-zone crash. Shared chrome
 * belongs in the shared chrome module.
 */
export function Header({ prompt, children }: { prompt: string | null; children: ReactNode }) {
  return (
    <header className="flex items-center gap-4 border-b-2 border-shell-rule bg-card/85 px-7 py-3.5 backdrop-blur-[8px]">
      <div className="flex shrink-0 items-center gap-2">
        <EggGlyph />
        <span className="font-chewy text-[22px] text-ember">Hatch</span>
      </div>
      {/* Four minutes into a build the user has lost the thread of what they
          asked for; the prompt is the answer to "what is this?". */}
      <p className="min-w-0 flex-1 truncate text-sm font-bold text-bark-soft" title={prompt ?? ""}>
        {prompt}
      </p>
      {children}
    </header>
  );
}

/** The fixed backdrop: gradient sky, sun, drifting clouds, and the cloud sea. */
export function Sky() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden bg-linear-180 from-paper-hi from-0% via-paper via-55% to-paper-lo to-100%"
    >
      {/*
        The sun's percentage offsets are read off a wide desktop window, where
        `top: 7%` is 63px of sky and `right: 9%` is well clear of a 980px column.
        Narrower than `lg` they stop meaning that: on a 390px phone 7% lands the
        disc exactly on the build screen's stage pills, and on a 768px tablet the
        hero grows to fill the width and runs "hatch it." straight through it. A
        pending pill in `bark-faint` over an orange disc is the one piece of text
        in this skin that stopped being readable.

        So below `lg` it is smaller and pinned to the corner in absolute units,
        where the only thing it can get behind is the translucent header — which
        it warms rather than fights.
      */}
      <div
        className="absolute top-2.5 right-2.5 size-[92px] rounded-full lg:top-[7%] lg:right-[9%] lg:size-[130px]"
        style={{
          background:
            "radial-gradient(circle,var(--color-sun) 0 46%,rgb(245 212 143 / 0.35) 47% 58%,transparent 62%)",
        }}
      />

      <Cloud className="top-[14%] left-[10%] h-9 w-[150px] opacity-85 animate-[drift1_22s_ease-in-out_infinite]" />
      <Cloud className="top-[12%] left-[16%] h-[30px] w-[90px] opacity-85 animate-[drift1_22s_ease-in-out_infinite]" />
      <Cloud className="top-[26%] right-[18%] h-8 w-[130px] opacity-70 animate-[drift2_28s_ease-in-out_infinite]" />
      <Cloud className="top-[7%] left-[34%] h-7 w-[110px] opacity-60 animate-[drift2_26s_ease-in-out_infinite]" />

      {/* Back strip of the sea, plus the solid fill it scallops out of. */}
      <div className="absolute inset-x-0 bottom-16 h-14" style={SCALLOP_BACK} />
      <div className="absolute inset-x-0 bottom-0 h-[70px] bg-cloud-back" />
      <CloudSeaFront className="absolute" />
    </div>
  );
}

/**
 * The front strip of the cloud sea, repeated above the content layer.
 *
 * The peak has to sit *in* the sea rather than on top of it, and the peak is
 * drawn above the backdrop so it can overlap the page's own content. So the
 * front strip is painted twice: once in the sky behind everything, once here in
 * front of the mountain's base.
 */
export function CloudSeaFront({ className = "" }: { className?: string }) {
  return (
    <div aria-hidden className={`pointer-events-none ${className}`}>
      <div className="fixed right-0 -left-[30px] bottom-6 h-12" style={SCALLOP_FRONT} />
      <div className="fixed inset-x-0 bottom-0 h-8 bg-cloud-front" />
    </div>
  );
}

function Cloud({ className }: { className: string }) {
  return <div className={`absolute rounded-[50%] bg-card ${className}`} />;
}

const SCALLOP_BACK = {
  background:
    "radial-gradient(circle 42px at 31px 46px,var(--color-cloud-back) 41px,transparent 42px)",
  backgroundSize: "62px 56px",
  backgroundRepeat: "repeat-x",
} as const;

const SCALLOP_FRONT = {
  background:
    "radial-gradient(circle 38px at 28px 42px,var(--color-cloud-front) 37px,transparent 38px)",
  backgroundSize: "56px 48px",
  backgroundRepeat: "repeat-x",
  zIndex: 1,
} as const;

/**
 * The peak the nest sits on: sandstone triangle, shaded right half, snowcap.
 *
 * Sized by its props, so the same component serves the landing screen's 460px
 * group and the building screen's 520px one. How much of that size actually gets
 * drawn is `.hatch-peak`'s business — see index.css, which shrinks the group when
 * the viewport is too narrow or too short to hold it clear of the content.
 */
export function Peak({
  width,
  height,
  mountainHeight,
  capWidth,
  capHeight,
  capBottom,
  placement,
  children,
}: {
  width: number;
  height: number;
  mountainHeight: number;
  capWidth: number;
  capHeight: number;
  capBottom: number;
  /**
   * `page` centres it on the base of whatever positioned box encloses it, and
   * carries the responsive scale ladder. Two screens want that, for the same
   * reason in two shapes:
   *
   *   The landing screen encloses it in the skin root, so the base is the bottom
   *   of the document. Its column cannot be made to fit above a viewport-pinned
   *   horizon on a short phone — hero, prompt card and six starter chips come to
   *   more than a 700px-tall window has left after the scenery, and pinned, the
   *   nest simply lands on the prompt card. On the page instead, the horizon is
   *   something you scroll down to and the collision cannot happen at any size.
   *
   *   The building screen encloses it in its scenery column, so the base is the
   *   bottom of that column — which, in a viewport-height frame, is the bottom
   *   of the viewport. The egg stays in view for however long the build takes
   *   without being `fixed` to do it, and it can no longer wander over the pane
   *   beside it, because it is not in that pane's box.
   *
   * `nested` sits it at the bottom-left of whatever box it is given, unscaled.
   * The done screen's corner vignette needs that: it positions its own
   * container and sizes the group itself.
   */
  placement: "page" | "nested";
  children?: ReactNode;
}) {
  return (
    <div
      aria-hidden
      className={PLACEMENT[placement]}
      style={{ width, height }}
    >
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-mountain"
        style={{ width, height: mountainHeight, clipPath: "polygon(50% 0,100% 100%,0 100%)" }}
      />
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-mountain-shade"
        style={{ width, height: mountainHeight, clipPath: "polygon(50% 0,100% 100%,50% 100%)" }}
      />
      <div
        className="absolute left-1/2 -translate-x-1/2 bg-card"
        style={{ width: capWidth, height: capHeight, bottom: capBottom, clipPath: SNOWCAP }}
      />
      {children}
    </div>
  );
}

const SNOWCAP =
  "polygon(50% 0,64% 26%,74% 58%,64% 48%,56% 70%,49% 46%,40% 66%,31% 44%,25% 56%)";

/**
 * `.hatch-peak` carries the centring translate along with the responsive scale,
 * because Tailwind's `-translate-x-1/2` would be overwritten by it.
 */
const PLACEMENT: Record<"page" | "nested", string> = {
  page: "hatch-peak pointer-events-none absolute bottom-[-10px] left-1/2 z-0 origin-[50%_100%]",
  // Anchored to the bottom of its container rather than stretched across it:
  // `Peak` sets its own width and height inline, so `inset-0` never stretched
  // anything — it just pinned the group to the container's TOP. That was
  // invisible while the container was exactly the group's size, and became the
  // done screen's bug the moment the container grew taller to hold the speech
  // bubble, which is measured from the group's base.
  nested: "pointer-events-none absolute bottom-0 left-0 z-0",
};

/**
 * A nest's measurements, in the coordinates of the `Peak` that holds it.
 *
 * Named as a type because two things read it now: the nest draws itself from
 * these numbers, and `perchOnNest` works out where the parent's feet go from the
 * same ones. Each screen declares its nest once and passes the object to both,
 * which is what stops the bird from drifting away from the rim it is supposed to
 * be standing on the next time a nest is resized.
 *
 * `bottom` is the ellipse's lowest point, matching the CSS: the outer ellipse
 * occupies `outerBottom … outerBottom + outerHeight`.
 */
export interface NestShape {
  outerWidth: number;
  outerHeight: number;
  outerBottom: number;
  innerWidth: number;
  innerHeight: number;
  innerBottom: number;
  twigScale?: number;
}

/** The width of `ParentDino`'s art board, which its feet sit at the centre of. */
const BIRD_BOX = 96;

/**
 * How far behind the front rim the parent's feet land, as a fraction of the
 * nest's depth. Small and positive: standing level with the rim reads as
 * standing in *front* of the nest, and any further back buries the feet.
 */
const FOOT_SINK = 0.155;

/**
 * Where the parent stands to be beside the egg rather than merely near it.
 *
 * `along` is how far out along the nest's right half to put the feet, 0 at the
 * centre and 1 at the rim. Everything else follows from the nest's own ellipse:
 * the feet land on the curve at that offset, so a bird placed this way cannot
 * float above the rim or sink through it, and cannot come loose when a nest is
 * resized for a new screen.
 *
 * This is not a new arrangement invented here — it is the one the design already
 * draws. Feeding it the done screen's vignette nest at `along: 0.455` returns
 * `left: calc(50% - 18px)` and `bottom: 146`, which are the design file's own
 * two numbers for that bird, to the pixel. The other two screens get the same
 * relationship rather than a fresh guess.
 */
export function perchOnNest(
  { outerWidth, outerHeight, outerBottom }: NestShape,
  along: number,
): { left: string; bottom: number } {
  const a = outerWidth / 2;
  const b = outerHeight / 2;
  // The ellipse's lower edge at this offset, which is the ground the bird stands
  // on. `along` is already a fraction of the semi-axis, so the ellipse equation
  // reduces to this.
  const rim = outerBottom + b - b * Math.sqrt(1 - along * along);
  // The art board is centred on the feet, so half of it comes back off.
  const offset = Math.round(a * along - BIRD_BOX / 2);

  return {
    // Written with a real sign rather than `+ -18px`. Both are valid `calc()`,
    // but only one of them reads as a position when it turns up in devtools.
    left: `calc(50% ${offset < 0 ? "-" : "+"} ${Math.abs(offset)}px)`,
    bottom: Math.round(rim + FOOT_SINK * outerHeight),
  };
}

/** Two stacked ellipses and three twigs. The egg sits in it. */
export function Nest({
  outerWidth,
  outerHeight,
  outerBottom,
  innerWidth,
  innerHeight,
  innerBottom,
  twigScale = 1,
}: NestShape) {
  const twig = (offset: number, bottom: number, w: number, rotate: number, dark = false) => (
    <div
      className={`absolute rounded-[3px] ${dark ? "bg-twig-dark" : "bg-twig"}`}
      style={{
        left: `calc(50% + ${offset}px)`,
        bottom,
        width: w,
        height: 5 * twigScale,
        transform: `rotate(${rotate}deg)`,
      }}
    />
  );

  return (
    <>
      <div
        className="absolute left-1/2 -translate-x-1/2 rounded-[50%] bg-nest"
        style={{ width: outerWidth, height: outerHeight, bottom: outerBottom }}
      />
      <div
        className="absolute left-1/2 -translate-x-1/2 rounded-[50%] bg-nest-deep"
        style={{ width: innerWidth, height: innerHeight, bottom: innerBottom }}
      />
      {twig(-outerWidth * 0.44, outerBottom + 6, 44 * twigScale, -9)}
      {twig(outerWidth * 0.16, outerBottom + 4, 48 * twigScale, 7)}
      {twig(-outerWidth * 0.12, outerBottom, 40 * twigScale, -4, true)}
    </>
  );
}

/** The design's egg silhouette — wider at the top than a plain ellipse. */
export const EGG_RADIUS = "50% 50% 50% 50% / 58% 58% 42% 42%";

/**
 * Sage speckles, positioned per egg size.
 *
 * Each is its own radial gradient stop rather than a child element, so the
 * speckles ride the egg's wobble without any of them needing a transform.
 */
export function speckledShell(speckles: Array<[x: number, y: number, r: number]>): string {
  const layers = speckles.map(
    ([x, y, r]) =>
      `radial-gradient(circle at ${x}% ${y}%,var(--color-speckle) 0 ${r}px,transparent ${r + 1}px)`,
  );
  return [...layers, "var(--color-shell)"].join(",");
}

/**
 * The small idling egg on the landing screen.
 *
 * Centred on the nest. The design file puts it at `left: calc(50% - 8px)` for a
 * 66px egg, which lands its centre 25px right of the nest's — a third of the way
 * out towards the rim, with the nest's left lobe left conspicuously bare. That
 * reads as a mistake rather than as composition, and it is the reason the parent
 * had nowhere to stand: the one side of the nest a bird beside the egg could
 * occupy was the side the egg had wandered into.
 */
export function LandingEgg() {
  return (
    <div
      className="absolute h-[82px] w-[66px] origin-[50%_90%] border-[2.5px] border-bark animate-[bob_3.4s_ease-in-out_infinite]"
      style={{
        left: "calc(50% - 33px)",
        bottom: 256,
        borderRadius: EGG_RADIUS,
        background: speckledShell([
          [34, 24, 4],
          [62, 44, 3],
          [36, 62, 3.5],
          [60, 76, 3],
        ]),
        boxShadow: "inset -6px -7px 0 rgb(62 50 38 / 0.08)",
      }}
    />
  );
}

/**
 * The four shards that make up the cracking.
 *
 * Each is a jagged clip-path polygon in ink, positioned on the shell. The last
 * two both belong to crack level 3 — the fourth is the small splinter that
 * makes the third crack read as a break rather than another line.
 */
const SHARDS = [
  {
    level: 1,
    left: 40,
    top: 32,
    width: 22,
    height: 37,
    rotate: 6,
    clip: "polygon(30% 0,58% 4%,100% 26%,50% 32%,80% 52%,28% 58%,88% 78%,52% 100%,40% 96%,70% 80%,12% 60%,62% 50%,32% 30%,86% 22%,42% 6%)",
  },
  {
    level: 2,
    left: 74,
    top: 25,
    width: 19,
    height: 48,
    rotate: -4,
    clip: "polygon(40% 0,66% 2%,30% 24%,90% 34%,42% 52%,96% 66%,54% 84%,78% 100%,58% 98%,30% 82%,72% 64%,16% 50%,66% 32%,8% 22%,52% 4%)",
  },
  {
    level: 3,
    left: 56,
    top: 65,
    width: 22,
    height: 48,
    rotate: 3,
    clip: "polygon(24% 0,52% 4%,90% 24%,40% 32%,74% 50%,20% 58%,82% 76%,44% 100%,30% 94%,64% 78%,6% 58%,58% 48%,26% 28%,76% 20%,34% 6%)",
  },
  {
    level: 3,
    left: 23,
    top: 73,
    width: 15,
    height: 24,
    rotate: 14,
    clip: "polygon(30% 0,60% 6%,100% 34%,46% 42%,78% 66%,40% 100%,26% 92%,60% 64%,10% 40%,64% 30%,36% 8%)",
  },
] as const;

/**
 * The big egg on the building screen, cracking as the build advances.
 *
 * Every shard is always in the DOM and revealed by opacity. Mounting them at
 * their thresholds instead would relayout the shell at each crack, and the one
 * thing the egg must never do is flinch for a reason that is not the build.
 *
 * `data-crack` is here for the tests: crack level is the whole point of this
 * component and asserting it through an opacity string would be testing CSS
 * rather than the mapping.
 */
export function BuildingEgg({
  crackLevel,
  animation,
  glow,
}: {
  crackLevel: number;
  animation: string;
  glow: boolean;
}) {
  return (
    <>
      {glow && (
        <div
          aria-hidden
          className="absolute bottom-[130px] left-1/2 h-[230px] w-[220px] -translate-x-1/2 rounded-full animate-[glowPulse_1.1s_ease-in-out_infinite]"
          style={{ background: "radial-gradient(circle,rgb(240 166 80 / 0.55),transparent 70%)" }}
          data-testid="egg-glow"
        />
      )}
      <div
        data-testid="building-egg"
        data-crack={crackLevel}
        className="absolute h-40 w-[130px] origin-[50%_92%]"
        // Centred, for the same reason as `LandingEgg`: the design's -60px on a
        // 130px egg is 5px off centre, which is invisible on its own and lines up
        // with nothing once the parent is standing on the other side.
        style={{ left: "calc(50% - 65px)", bottom: 206, animation }}
      >
        <div
          className="absolute inset-0 border-[3px] border-bark"
          style={{
            borderRadius: EGG_RADIUS,
            background: speckledShell([
              [34, 24, 6],
              [66, 38, 5],
              [28, 52, 5],
              [58, 64, 7],
              [44, 84, 5],
            ]),
            boxShadow: "inset -10px -12px 0 rgb(62 50 38 / 0.08)",
          }}
        />
        {SHARDS.map((shard, i) => (
          <div
            key={i}
            data-testid={`crack-${i + 1}`}
            data-shown={crackLevel >= shard.level ? "true" : "false"}
            className="absolute bg-bark transition-opacity duration-300"
            style={{
              left: shard.left,
              top: shard.top,
              width: shard.width,
              height: shard.height,
              transform: `rotate(${shard.rotate}deg)`,
              clipPath: shard.clip,
              opacity: crackLevel >= shard.level ? 1 : 0,
            }}
          />
        ))}
      </div>
    </>
  );
}

/**
 * The parent pterodactyl, drawn in profile facing left.
 *
 * Three screens want the same bird at three sizes and three head angles — the
 * landing screen's watcher on the ridge, the building screen's fretting parent,
 * and the done screen's corner vignette — so the body lives here with the rest
 * of the scenery rather than beside any one of them.
 *
 * `scale` is applied on a wrapper, not on the bobbing element. The design file
 * puts `transform:scale(.72)` on the same node as `animation:dinoBob`, where the
 * keyframe's own `transform: translateY(…)` wins outright and the scale is
 * simply never applied — except under reduced motion, where the animation drops
 * out and the bird abruptly shrinks. Splitting the two makes `scale` mean what
 * it says at every setting.
 *
 * `left` and `bottom` should come from `perchOnNest` rather than being written
 * out. The art board is 96×96 with the feet at its bottom centre, and scaling
 * about `50% 100%` keeps them there at any size — which is what lets one rule
 * place this bird on three differently-sized nests.
 *
 * @param headTilt degrees, negative tilts the head back so he looks upward.
 *   Past about -35 the crest swings clear of the skull and reads as a loose
 *   shard rather than a crest, so the three screens stay inside that.
 */
export function ParentDino({
  left,
  bottom,
  scale,
  headTilt,
}: {
  left: string;
  bottom: number;
  scale: number;
  headTilt: number;
}) {
  return (
    <div
      aria-hidden
      className="absolute size-24 origin-[50%_100%]"
      style={{ left, bottom, transform: `scale(${scale})` }}
    >
      <div className="absolute inset-0 animate-[dinoBob_3s_ease-in-out_infinite]">
        <div className="absolute left-10 top-[78px] h-4 w-1.5 rounded-[3px] bg-beak" />
        <div className="absolute left-14 top-[78px] h-4 w-1.5 rounded-[3px] bg-beak" />
        <div
          className="absolute left-7 top-[30px] h-[52px] w-[50px] border-[2.5px] border-bark bg-dino"
          style={{ borderRadius: "50% 50% 48% 48% / 56% 56% 44% 44%" }}
        />
        <div className="absolute left-[42px] top-11 h-5 w-8 -rotate-[22deg] rounded-[50%] bg-dino-shade" />
        {/* Head group: beak, crest, skull, and the eye that blinks. */}
        <div
          className="absolute inset-0"
          style={{ transform: `rotate(${headTilt}deg)`, transformOrigin: "42px 36px" }}
        >
          <div
            className="absolute left-9 top-0.5 h-[13px] w-[26px] -rotate-[8deg] bg-beak"
            style={{ clipPath: "polygon(0 90%,8% 8%,100% 70%)" }}
          />
          <div
            className="absolute left-0.5 top-[22px] h-2.5 w-[22px] bg-beak"
            style={{ clipPath: "polygon(0 45%,100% 0,100% 100%)" }}
          />
          {/* 30×28, not a circle. The flattening is the design's, and it is what
              keeps head and body from reading as two stacked snowballs. */}
          <div className="absolute left-[18px] top-2.5 h-7 w-[30px] rounded-full border-[2.5px] border-bark bg-dino" />
          <div className="absolute left-7 top-[15px] h-[11px] w-2.5 rounded-full border-2 border-bark bg-shell animate-[blinkEye_5s_ease-in-out_infinite]">
            <div className="absolute left-0.5 top-px h-[5px] w-1 rounded-full bg-bark" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** The header/logo glyph: a small plain egg. */
export function EggGlyph({ className = "h-5 w-4 border-2" }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={`shrink-0 border-bark bg-shell ${className}`}
      style={{ borderRadius: EGG_RADIUS }}
    />
  );
}
